import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface TrackingNumber {
  id: string;
  ctm_number_id: string;
  phone_e164: string;
  label: string;
  purpose: 'main' | 'google_ads_pool' | 'organic' | 'direct' | 'offline' | 'custom';
  slot: number;
  answer_mode: 'forward' | 'softphone';
  forward_to_e164: string | null;
  status: 'active' | 'paused' | 'released';
  routing_pending: boolean;
  recording_enabled: boolean;
  /** Australian local numbers are voice-only; US numbers can text. */
  sms_enabled: boolean;
  is_test: boolean;
  monthly_cost_cents: number;
  next_billing_date: string | null;
  source_name: string | null;
  campaign_id: string | null;
  campaign_name: string | null;
  calls_this_month: number;
  texts_this_month: number;
  purchased_at: string | null;
}

export interface ProvisioningStatus {
  status: 'running' | 'succeeded' | 'failed' | 'partial';
  failed_step: string | null;
  error: string | null;
  started_at: string;
  finished_at: string | null;
  numbers: number;
}

export interface SiteCheckResult {
  checked_url: string | null;
  reachable: boolean;
  reason?: string;
  status?: number;
  script_found?: boolean;
  script_evidence?: string | null;
  numbers_found?: string[];
  numbers_missing?: string[];
  healthy?: boolean;
  message: string;
}

export const TRACKING_NUMBERS_KEY = ['tracking-numbers'] as const;
const PROVISIONING_KEY = ['provisioning-status'] as const;

/** The tenant's Steddy-managed phone numbers, and the one edit they can make. */
export function useTrackingNumbers(enabled = true) {
  const queryClient = useQueryClient();

  const numbersQuery = useQuery<TrackingNumber[]>({
    queryKey: TRACKING_NUMBERS_KEY,
    enabled,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_tracking_numbers');
      if (error) throw error;
      return (data ?? []) as TrackingNumber[];
    },
  });

  const provisioningQuery = useQuery<ProvisioningStatus | null>({
    queryKey: PROVISIONING_KEY,
    enabled,
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_provisioning_status');
      if (error) throw error;
      return ((data ?? [])[0] as ProvisioningStatus) ?? null;
    },
  });

  const updateRouting = useMutation({
    mutationFn: async (input: { id: string; answerMode: 'forward' | 'softphone'; forwardTo?: string | null }) => {
      const { error } = await supabase.rpc('update_tracking_number_routing', {
        p_id: input.id,
        p_answer_mode: input.answerMode,
        p_forward_to: input.forwardTo ?? null,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TRACKING_NUMBERS_KEY });
    },
  });

  const checkSite = useMutation({
    mutationFn: async (url?: string) => {
      const { data, error } = await supabase.functions.invoke('check-tracking-script', {
        body: url ? { url } : {},
      });
      if (error) {
        const ctx = (error as { context?: Response }).context;
        let detail = error.message ?? 'The check could not run.';
        try {
          const body = ctx ? await ctx.clone().json() : null;
          if (body?.error) detail = String(body.error);
        } catch {
          /* keep the generic message */
        }
        throw new Error(detail);
      }
      return data as SiteCheckResult;
    },
  });

  return {
    numbers: numbersQuery.data ?? [],
    isLoading: numbersQuery.isLoading,
    error: numbersQuery.error,
    refetch: numbersQuery.refetch,
    provisioning: provisioningQuery.data ?? null,
    updateRouting: updateRouting.mutateAsync,
    isSavingRouting: updateRouting.isPending,
    checkSite: checkSite.mutateAsync,
    isCheckingSite: checkSite.isPending,
    siteCheck: checkSite.data ?? null,
  };
}
